import { useCallback } from 'react';

import { useMixpanel } from 'context/mixpanel-context';

/** Where a banner sat in the recipient's queue. Never carries the banner's title or body. */
export interface BannerEventProperties {
  banner_id: string;
  queue_position: number;
  queue_size: number;
}

export interface AthleteEventProperties {
  athlete_class_reserved?: {
    class_id: string;
    class_name: string;
    duration_minutes: number;
  };

  athlete_class_cancelled?: {
    class_id: string;
    class_name: string;
    is_late_cancellation: boolean;
    credit_deduction: boolean;
    late_fee: number;
  };

  athlete_appointment_cancelled?: {
    appointment_id: string;
    appointment_name: string;
    is_late_cancellation: boolean;
    credit_deduction: boolean;
    late_fee: number;
  };

  athlete_packages_tab_selected?: {
    tab_selected: string;
    source_tab: string;
  };

  athlete_invoice_pay?: {
    invoice_id: string;
    purchase_amount: number;
    due_date: string;
    autopay: boolean;
  };

  athlete_send_attendance?: {
    start_date: string;
    event_type: string;
  };

  athlete_add_payment_method?: {
    payment_method_name: string;
  };

  athlete_payment_method_delete_clicked?: {
    reason?: string | null;
  };

  athlete_payment_method_add_new_clicked?: {
    reason?: string | null;
  };

  athlete_leaderboard_opened?: {
    workout_id: string;
    workout_name: string;
  };

  athlete_leaderboard_date_filter_changed?: {
    value: 'this-session' | 'today';
  };

  athlete_leaderboard_gender_filter_changed?: {
    value: 'all' | 'men' | 'women';
  };

  athlete_leaderboard_category_filter_changed?: {
    value: string;
  };

  athlete_leaderboard_filter_sheet_opened?: Record<string, never>;

  athlete_leaderboard_filter_sheet_applied?: {
    date: 'this-session' | 'today';
    gender: 'all' | 'men' | 'women';
    level: string;
  };

  athlete_leaderboard_your_ranking_card_tapped?: {
    rank: number | null;
    is_ranked: boolean;
  };

  athlete_reservation_blocked_by_daily_limit?: {
    event_kind: 'classes' | 'appointments';
    held_by: 'reservation' | 'waitlist' | 'check_in';
  };

  athlete_banner_viewed?: BannerEventProperties;

  athlete_banner_dismissed?: BannerEventProperties;

  athlete_banner_link_clicked?: BannerEventProperties;
}

export const useAthleteTracking = (): any => {
  const { track } = useMixpanel();

  const trackClassReserved = useCallback(
    (classId: string, className: string, duration: number) => {
      track('athlete_class_reserved', {
        athlete_class_reserved: {
          class_id: classId,
          class_name: className,
          duration_minutes: duration,
        },
      });
    },
    [track],
  );

  const trackClassCancelled = useCallback(
    (
      classId: string,
      className: string,
      isLateCancellation: boolean,
      creditDeduction: boolean,
      lateFee: number,
    ) => {
      track('athlete_class_cancelled', {
        athlete_class_cancelled: {
          class_id: classId,
          class_name: className,
          is_late_cancellation: isLateCancellation,
          credit_deduction: creditDeduction,
          late_fee: lateFee,
        },
      });
    },
    [track],
  );

  const trackAppointmentCancelled = useCallback(
    (
      appointmentId: string,
      appointmentName: string,
      isLateCancellation: boolean,
      creditDeduction: boolean,
      lateFee: number,
    ) => {
      track('athlete_appointment_cancelled', {
        athlete_appointment_cancelled: {
          appointment_id: appointmentId,
          appointment_name: appointmentName,
          is_late_cancellation: isLateCancellation,
          credit_deduction: creditDeduction,
          late_fee: lateFee,
        },
      });
    },
    [track],
  );

  const trackInvoicePay = useCallback(
    (invoiceId: string, purchaseAmount: number, dueDate: string, autopay: boolean) => {
      track('athlete_invoice_pay', {
        athlete_invoice_pay: {
          invoice_id: invoiceId,
          purchase_amount: purchaseAmount,
          due_date: dueDate,
          autopay: autopay,
        },
      });
    },
    [track],
  );

  const trackSendAttendance = useCallback(
    (startDate: string, eventType: string) => {
      track('athlete_send_attendance', {
        athlete_send_attendance: {
          start_date: startDate,
          event_type: eventType,
        },
      });
    },
    [track],
  );

  const trackAddPaymentMethod = useCallback(
    (paymentMethodName: string) => {
      track('athlete_add_payment_method', {
        athlete_add_payment_method: {
          payment_method_name: paymentMethodName,
        },
      });
    },
    [track],
  );

  const trackPackagesTabSelected = useCallback(
    (tabSelected: string, sourceTab: string) => {
      track('athlete_packages_tab_selected', {
        athlete_packages_tab_selected: {
          tab_selected: tabSelected,
          source_tab: sourceTab,
        },
      });
    },
    [track],
  );

  const trackPaymentMethodDeleteClicked = useCallback(
    (reason?: string | null) => {
      track('athlete_payment_method_delete_clicked', {
        athlete_payment_method_delete_clicked: { reason },
      });
    },
    [track],
  );

  const trackPaymentMethodAddNewClicked = useCallback(
    (reason?: string | null) => {
      track('athlete_payment_method_add_new_clicked', {
        athlete_payment_method_add_new_clicked: { reason },
      });
    },
    [track],
  );

  const trackLeaderboardOpened = useCallback(
    (props: NonNullable<AthleteEventProperties['athlete_leaderboard_opened']>) => {
      track('athlete_leaderboard_opened', { athlete_leaderboard_opened: props });
    },
    [track],
  );

  const trackLeaderboardFilterSheetOpened = useCallback(() => {
    track('athlete_leaderboard_filter_sheet_opened', { athlete_leaderboard_filter_sheet_opened: {} });
  }, [track]);

  const trackLeaderboardDateFilterChanged = useCallback(
    (props: NonNullable<AthleteEventProperties['athlete_leaderboard_date_filter_changed']>) => {
      track('athlete_leaderboard_date_filter_changed', { athlete_leaderboard_date_filter_changed: props });
    },
    [track],
  );

  const trackLeaderboardGenderFilterChanged = useCallback(
    (props: NonNullable<AthleteEventProperties['athlete_leaderboard_gender_filter_changed']>) => {
      track('athlete_leaderboard_gender_filter_changed', {
        athlete_leaderboard_gender_filter_changed: props,
      });
    },
    [track],
  );

  const trackLeaderboardCategoryFilterChanged = useCallback(
    (props: NonNullable<AthleteEventProperties['athlete_leaderboard_category_filter_changed']>) => {
      track('athlete_leaderboard_category_filter_changed', {
        athlete_leaderboard_category_filter_changed: props,
      });
    },
    [track],
  );

  const trackLeaderboardFilterSheetApplied = useCallback(
    (props: NonNullable<AthleteEventProperties['athlete_leaderboard_filter_sheet_applied']>) => {
      track('athlete_leaderboard_filter_sheet_applied', {
        athlete_leaderboard_filter_sheet_applied: props,
      });
    },
    [track],
  );

  const trackLeaderboardYourRankingCardTapped = useCallback(
    (props: NonNullable<AthleteEventProperties['athlete_leaderboard_your_ranking_card_tapped']>) => {
      track('athlete_leaderboard_your_ranking_card_tapped', {
        athlete_leaderboard_your_ranking_card_tapped: props,
      });
    },
    [track],
  );

  const trackReservationBlockedByDailyLimit = useCallback(
    (eventKind: 'classes' | 'appointments', heldBy: 'reservation' | 'waitlist' | 'check_in') => {
      track('athlete_reservation_blocked_by_daily_limit', {
        athlete_reservation_blocked_by_daily_limit: {
          event_kind: eventKind,
          held_by: heldBy,
        },
      });
    },
    [track],
  );

  const trackBannerViewed = useCallback(
    (props: BannerEventProperties) => {
      track('athlete_banner_viewed', { athlete_banner_viewed: props });
    },
    [track],
  );

  const trackBannerDismissed = useCallback(
    (props: BannerEventProperties) => {
      track('athlete_banner_dismissed', { athlete_banner_dismissed: props });
    },
    [track],
  );

  const trackBannerLinkClicked = useCallback(
    (props: BannerEventProperties) => {
      track('athlete_banner_link_clicked', { athlete_banner_link_clicked: props });
    },
    [track],
  );

  return {
    trackClassReserved,
    trackClassCancelled,
    trackAppointmentCancelled,
    trackInvoicePay,
    trackSendAttendance,
    trackAddPaymentMethod,
    trackPackagesTabSelected,
    trackPaymentMethodDeleteClicked,
    trackPaymentMethodAddNewClicked,
    trackLeaderboardOpened,
    trackLeaderboardFilterSheetOpened,
    trackLeaderboardDateFilterChanged,
    trackLeaderboardGenderFilterChanged,
    trackLeaderboardCategoryFilterChanged,
    trackLeaderboardFilterSheetApplied,
    trackLeaderboardYourRankingCardTapped,
    trackReservationBlockedByDailyLimit,
    trackBannerViewed,
    trackBannerDismissed,
    trackBannerLinkClicked,
  };
};
